import type { NonNullableFormBuilder } from '@angular/forms';
import { Validators } from '@angular/forms';
import { type ControlInterface, enumValidator, selectControlValue } from '@comparit/core';
import { tap } from 'rxjs';
import type { Step9FormInput, VersicherungssummeModus } from '../../../interfaces';
import { HausratSelbstbeteiligung } from '../../../interfaces';

/** The Sparte takes an eigene Versicherungssumme of 1 to 900.000 €. */
const VERSICHERUNGSSUMME_MIN = 1;
const VERSICHERUNGSSUMME_MAX = 900000;

export class Step9Form {
  constructor(
    private fb: NonNullableFormBuilder,
    private defaultValue?: Step9FormInput | null
  ) {
    this.versicherungssummeValidation$.subscribe();
  }

  ctrls: ControlInterface<Step9FormInput> = {
    versicherungssumme: this.fb.control<number | null>(
      this.defaultValue?.versicherungssumme ?? null
    ),
    selbstbeteiligung: this.fb.control<HausratSelbstbeteiligung | null>(
      this.defaultValue?.selbstbeteiligung ?? HausratSelbstbeteiligung.Eur0,
      [Validators.required, enumValidator(HausratSelbstbeteiligung)]
    ),
    // The Sparte's own default: most Wohnungs- und Hausabschlusstüren meet the Mindestsicherung.
    mindestsicherung: this.fb.control<boolean | null>(this.defaultValue?.mindestsicherung ?? true),
    ueberwachungsanlage: this.fb.control<boolean | null>(
      this.defaultValue?.ueberwachungsanlage ?? false
    ),
    // Inside the group so it is persisted: picking "Eigene Vorgabe" without typing a sum yet is a
    // real state, and deriving it from `versicherungssumme != null` loses it on reload.
    versicherungssummeModus: this.fb.control<VersicherungssummeModus>(
      this.defaultValue?.versicherungssummeModus ??
        (this.defaultValue?.versicherungssumme != null ? 'eigene' : 'auto')
    ),
  };

  public versicherungssummeModus$ = selectControlValue(this.ctrls.versicherungssummeModus);

  /**
   * Without an eigene Vorgabe the field has to go on the wire absent, not as a stale number —
   * every Tarif then derives the sum from the Wohnfläche, with Unterversicherungsverzicht.
   */
  private versicherungssummeValidation$ = this.versicherungssummeModus$.pipe(
    tap(modus => {
      if (modus === 'eigene') {
        this.ctrls.versicherungssumme.setValidators([
          Validators.required,
          Validators.min(VERSICHERUNGSSUMME_MIN),
          Validators.max(VERSICHERUNGSSUMME_MAX),
        ]);
      } else {
        this.ctrls.versicherungssumme.clearValidators();
        if (this.ctrls.versicherungssumme.value !== null) {
          this.ctrls.versicherungssumme.setValue(null);
        }
      }
      this.ctrls.versicherungssumme.updateValueAndValidity();
    })
  );

  public fg = this.fb.group(this.ctrls);
}
